<template>
  <div class="rvm-body">

    <!-- Draft section -->
    <el-divider content-position="left">Current draft (unpublished)</el-divider>
    <p class="rvm-caption">This is the live, editable rate. Publish to mint an immutable version, or revert to discard edits.</p>

    <rate-tiers-cell :row-data="draftRowData" :hide-drawer-affordance="true" />

    <div class="rvm-actions">
      <el-tooltip
        placement="top"
        :disabled="hasPublishedVersions"
        content="Nothing to revert to — no version has been published yet.">
        <span>
          <el-button
            icon="fa fa-undo"
            :disabled="!hasPublishedVersions || acting"
            aria-label="Revert draft"
            title="Revert draft"
            @click="onRevert">
            Revert
          </el-button>
        </span>
      </el-tooltip>
      <el-button
        type="primary"
        icon="fa fa-cloud-upload"
        :disabled="acting"
        aria-label="Publish draft"
        title="Publish draft"
        @click="onPublish">
        Publish
      </el-button>
    </div>

    <el-alert
      v-if="resumingRevert"
      class="rvm-revert-note"
      type="info"
      :closable="false"
      show-icon
      title=""
      :description="'Draft reverted to ' + revertedToLabel + '. Fees for sharers following this rate are recalculating in the background — this may take a moment.'" />

    <!-- Version history section -->
    <el-divider content-position="left">Version history</el-divider>
    <p class="rvm-caption">Published versions are immutable history. They are removed only if the rate itself is deleted (blocked while any site uses the rate).</p>

    <div v-loading="loading">
      <el-alert
        v-if="errored"
        type="error"
        :closable="false"
        show-icon
        title="Couldn't load version history"
        description="Something went wrong loading versions. Close the drawer and try again." />

      <div
        v-else-if="loaded && versions.length === 0"
        class="rvm-empty">
        No published versions yet — Publish the draft to create v1.
      </div>

      <rate-version-history
        v-else-if="loaded && versions.length > 0"
        :versions="versions" />
    </div>

  </div>
</template>

<script>
import axios from '@/lib/axiosClient'
import NumberMixins from '@/mixins/NumberMixins'
import PostMixins from '@/mixins/PostMixins'
import DateMixins from '@/mixins/DateMixins'

import RateTiersCell from './RateTiersCell'
import RateVersionHistory from './RateVersionHistory'

export default {
  name: 'rate-version-manager',

  mixins: [NumberMixins, PostMixins, DateMixins],

  components: {
    RateTiersCell,
    RateVersionHistory
  },

  props: {
    rowData: {
      type: Object,
      required: true
    },

    tableRef: {
      type: String,
      default: null
    }
  },

  data: function () {
    return {
      versions: [],
      loading: false,
      loaded: false,
      errored: false,
      acting: false,
      resumingRevert: false,
      revertedToLabel: '',
      liveTiers: (this.rowData && this.rowData.tiers) ? this.rowData.tiers.slice() : []
    }
  },

  computed: {
    // XOL-487: carry the band through from rowData (the prop, so it stays
    // live if the parent updates it — never snapshot into data()). Before
    // this fix, draftRowData discarded fromSharer/toSharer entirely,
    // which was the reported bug's actual site: the draft cell fell back to
    // RateTiersCell's absent-band default ([1..10]) regardless of the real
    // band. Revert restores tiers only (never the header band), so no
    // re-sync is needed on revert. Guarded the same way as the sibling
    // rateName computed below — rowData is required but can still arrive
    // null (see RateTiersCell's own isInactive guard).
    draftRowData: function () {
      var row = this.rowData || {}
      return {
        tiers: this.liveTiers,
        fromSharer: row.fromSharer,
        toSharer: row.toSharer
      }
    },

    hasPublishedVersions: function () {
      return this.versions.length > 0
    },

    rateName: function () {
      return this.rowData ? this.rowData.name : ''
    }
  },

  methods: {
    open: function () {
      this.liveTiers = (this.rowData && this.rowData.tiers) ? this.rowData.tiers.slice() : []
      this.resumingRevert = false
      this.load()
    },

    load: function () {
      if (!this.rowData || !this.rowData.id) return

      var self = this
      this.loading = true
      this.errored = false
      this.loaded = false

      // Raw axios (not fetchData) so a failed load shows ONLY the calm in-drawer
      // alert — no redundant global "Unable to fetch…" error toast.
      axios.get(this.getEndpoint('/rates/' + this.rowData.id + '/versions'))
        .then(function (response) {
          var body = response ? response.data : null
          var data = (body && body.data) ? body.data : body
          self.versions = Array.isArray(data) ? data : []
          self.loading = false
          self.loaded = true
        })
        .catch(function (_err) {
          self.errored = true
          self.loading = false
          self.loaded = true
        })
    },

    currentVersionNo: function () {
      var found = this.versions.find(function (v) { return v.is_current })
      return found ? found.version_no : null
    },

    onPublish: function () {
      var self = this
      var name = this.rateName

      this.$confirm(
        'Publishing mints a <b>new immutable version</b> of "' + name + '". This happens <b>every time you publish</b> — it is not idempotent, and creates a fresh version even if nothing changed. Continue?',
        'Publish version',
        {
          confirmButtonText: 'Publish',
          cancelButtonText: 'Cancel',
          center: true,
          dangerouslyUseHTMLString: true,
          type: 'warning'
        }
      ).then(function () {
        self.acting = true
        self.send(
          self,
          self.getEndpoint('/rates/' + self.rowData.id + '/publish'),
          {},
          'rate version',
          function (_data) {
            self.acting = false
            self.load()
            self.$emit('changed')
          },
          function (_err) {
            self.acting = false
          },
          axios.post,
          true,
          'Publish',
          self.tableRef,
          true
        )
      }).catch(function () {
        // cancelled — no-op
      })
    },

    onRevert: function () {
      if (!this.hasPublishedVersions) return

      var self = this
      var currentNo = this.currentVersionNo()

      this.$confirm(
        'This discards the current unpublished edits and restores the draft to <b>v' + (currentNo || '?') + '</b>. The discarded edits cannot be recovered. Continue?',
        'Revert draft',
        {
          confirmButtonText: 'Revert',
          cancelButtonText: 'Cancel',
          center: true,
          dangerouslyUseHTMLString: true,
          type: 'error'
        }
      ).then(function () {
        self.acting = true
        self.send(
          self,
          self.getEndpoint('/rates/' + self.rowData.id + '/revert'),
          {},
          'rate',
          function (data) {
            self.liveTiers = (data && data.tiers) ? data.tiers : self.liveTiers
            self.acting = false
            // Revert dispatches an async fee-recompute for "Latest" (unversioned)
            // sharers following this rate — surface the lag (mirrors XOL-305).
            self.revertedToLabel = 'v' + (currentNo || '?')
            self.resumingRevert = true
            self.load()
            self.$emit('changed')
          },
          function (_err) {
            self.acting = false
          },
          axios.post,
          true,
          'Revert',
          self.tableRef,
          true
        )
      }).catch(function () {
        // cancelled — no-op
      })
    }
  }
}
</script>

<style scoped>
.rvm-body {
  text-align: left;
  overflow: auto;
  padding: 4px 20px 24px;
}

.rvm-caption {
  color: #909399;
  font-size: 12px;
  margin: 0 0 10px;
}

.rvm-actions {
  text-align: right;
  margin-top: 10px;
}

.rvm-empty {
  text-align: center;
  color: #909399;
  padding: 24px 0;
}

.rvm-revert-note {
  margin-top: 10px;
}

/* The drawer is 96.3% wide; RateTiersCell is built for a narrow table cell.
   Constrain it to a readable width and pin the leading two columns to fixed
   widths so the rate values line up across tier rows (the shared cell uses
   per-row `auto` columns that stagger when stretched). Scoped to the drawer
   via ::v-deep — the Settings table render is untouched. */
.rvm-body ::v-deep .rate-tiers-cell {
  max-width: 900px;
}

.rvm-body ::v-deep .rtc-grid {
  grid-template-columns: 36px 160px repeat(3, minmax(0, 1fr));
  grid-template-columns: 36px 160px repeat(var(--rtc-cols-narrow, 3), minmax(0, 1fr));
}

@media (min-width: 1200px) {
  .rvm-body ::v-deep .rtc-grid {
    grid-template-columns: 36px 160px repeat(6, minmax(0, 1fr));
    grid-template-columns: 36px 160px repeat(var(--rtc-cols, 6), minmax(0, 1fr));
  }
}
</style>
